import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { projectsApi } from '@/api/projects'
import { usersApi } from '@/api/users'
import { phasesApi } from '@/api/sections'
import { useEffectiveRole } from '@/store/auth'
import { DOC_TYPES, DOC_TYPE_LABELS } from '@/types'
import type { DocType } from '@/types'
import { X } from 'lucide-react'
import clsx from 'clsx'
import type { ProjectOut, ProjectPatch, ProjectColor, ProjectLifecycleStage } from '@/types'
import CharCounter from '@/components/common/CharCounter'
import MoneyInput from '@/components/common/MoneyInput'

interface Props {
  project: ProjectOut
  onClose: () => void
}

const LIFECYCLE_OPTIONS: { value: ProjectLifecycleStage; label: string; cls: string }[] = [
  { value: 'pre_project', label: 'Предпроект',  cls: 'border-gray-300 text-gray-600 hover:bg-gray-50' },
  { value: 'in_design',   label: 'В работе',    cls: 'border-blue-300 text-blue-700 hover:bg-blue-50' },
  { value: 'post_design', label: 'Завершён',    cls: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' },
]

const COLOR_OPTIONS: { value: ProjectColor | 'auto'; label: string; dot: string; ring: string }[] = [
  { value: 'auto',   label: 'Авто',          dot: 'bg-gray-300',     ring: 'ring-gray-300' },
  { value: 'green',  label: 'В сроках',      dot: 'bg-emerald-500',  ring: 'ring-emerald-400' },
  { value: 'yellow', label: 'Скоро дедлайн', dot: 'bg-amber-400',    ring: 'ring-amber-400' },
  { value: 'red',    label: 'Просрочка',     dot: 'bg-rose-500',     ring: 'ring-rose-400' },
  { value: 'grey',   label: 'Приостановлен', dot: 'bg-gray-400',     ring: 'ring-gray-400' },
]

const schema = z.object({
  code:             z.string().trim().min(1, 'Укажите шифр проекта').max(50, 'Максимум 50 символов'),
  name:             z.string().min(3, 'Минимум 3 символа').max(300, 'Максимум 300 символов'),
  customer:         z.string().min(2, 'Укажите заказчика').max(300, 'Максимум 300 символов'),
  address:          z.string().min(5, 'Укажите адрес').max(500, 'Максимум 500 символов'),
  start_date:       z.string().min(1, 'Укажите дату начала'),
  planned_end_date: z.string().min(1, 'Укажите срок'),
  chief_engineer_id: z.string().optional(),
  lifecycle_stage:  z.enum(['pre_project', 'in_design', 'post_design']),
  manual_color:     z.enum(['auto', 'green', 'yellow', 'red', 'grey']),
  note:             z.string().max(1000, 'Максимум 1000 символов').optional(),
  cost: z
    .string()
    .optional()
    .refine((v) => !v || (!isNaN(Number(v)) && Number(v) >= 0), {
      message: 'Введите корректную сумму',
    }),
})

type FormData = z.infer<typeof schema>

export default function EditProjectModal({ project, onClose }: Props) {
  const qc = useQueryClient()
  const overlayRef = useRef<HTMLDivElement>(null)
  const role = useEffectiveRole()
  const isDirectorFamily = role === 'director' || role === 'deputy_director'
  // Даты договора проекта (срок) меняет только директор/зам
  const canEditContractDates = isDirectorFamily
  // Деньги (договорная стоимость, стоимости этапов) видит и правит только директор/зам
  const showCost = isDirectorFamily

  type PhaseDraft = { start_date: string; end_date: string; cost: string }
  const [phaseDrafts, setPhaseDrafts] = useState<Record<string, PhaseDraft>>({})
  const [saveError,   setSaveError]   = useState('')

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
  })
  const chiefEngineers = users.filter((u) => u.role === 'chief_engineer' && u.is_active)

  const { data: allProjects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
  })

  const { data: phases = [] } = useQuery({
    queryKey: ['phases', project.id],
    queryFn:  () => phasesApi.list(project.id).then((r) => r.data),
  })

  // Инициализируем черновики фаз при загрузке
  useEffect(() => {
    if (!phases.length) return
    const drafts: Record<string, PhaseDraft> = {}
    for (const ph of phases) {
      drafts[ph.doc_type] = {
        start_date: ph.start_date ?? '',
        end_date:   ph.end_date   ?? '',
        cost:       ph.cost       ?? '',
      }
    }
    setPhaseDrafts(drafts)
  }, [phases])

  const updateMutation = useMutation({
    mutationFn: (data: ProjectPatch) => projectsApi.patch(project.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', project.id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['gantt'] })
      qc.invalidateQueries({ queryKey: ['dashboard-zones'] })
      onClose()
    },
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      code:              project.code,
      name:              project.name,
      customer:          project.customer,
      address:           project.address,
      start_date:        project.start_date,
      planned_end_date:  project.planned_end_date,
      chief_engineer_id: project.chief_engineer_id ?? '',
      lifecycle_stage:   project.lifecycle_stage as FormData['lifecycle_stage'],
      manual_color:      (project.manual_color as FormData['manual_color']) ?? 'auto',
      note:              project.note ?? '',
      cost:              project.cost ?? '',
    },
  })

  const codeValue = watch('code')
  const codeConflict = allProjects.find((p) => p.code === codeValue?.trim() && p.id !== project.id)

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose()
  }
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function onSubmit(data: FormData) {
    setSaveError('')
    const patch: ProjectPatch = {
      name:              data.name,
      customer:          data.customer,
      address:           data.address,
      chief_engineer_id: data.chief_engineer_id || null,
      lifecycle_stage:   data.lifecycle_stage as ProjectLifecycleStage,
      // 'auto' → null (сбросить ручной override), иначе — значение цвета
      manual_color: data.manual_color === 'auto'
        ? null
        : data.manual_color as ProjectColor,
      // Примечание: пустая строка очищает примечание
      note: (data.note ?? '').trim(),
      // ГИП не передаёт cost — стоимость остаётся как ввёл директор
      ...(showCost ? { cost: data.cost ? data.cost : null } : {}),
    }
    // Шифр и даты договора проекта меняет только директор/зам
    if (canEditContractDates) {
      patch.code = data.code.trim()
      patch.start_date = data.start_date
      patch.planned_end_date = data.planned_end_date
    }
    try {
      // 1. Сохраняем основной патч проекта
      await projectsApi.patch(project.id, patch)
      // 2. Сохраняем фазы параллельно
      await Promise.all(
        DOC_TYPES.map((dt) => {
          const draft = phaseDrafts[dt]
          if (!draft) return Promise.resolve()
          return phasesApi.patch(project.id, dt, {
            start_date: draft.start_date || null,
            end_date:   draft.end_date   || null,
            ...(showCost ? { cost: draft.cost || null } : {}),
          })
        })
      )
      // 3. Инвалидируем кэш и закрываем
      qc.invalidateQueries({ queryKey: ['project', project.id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['phases', project.id] })
      qc.invalidateQueries({ queryKey: ['gantt'] })
      qc.invalidateQueries({ queryKey: ['dashboard-zones'] })
      onClose()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (detail?.code === 'project_code_exists') {
        setSaveError(`Шифр уже используется в проекте «${detail.project_name}» (${detail.project_code})`)
      } else {
        setSaveError('Ошибка при сохранении. Попробуйте ещё раз.')
      }
    }
  }

  const inputCls =
    'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1'
  const errorCls = 'mt-1 text-xs text-rose-600'

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="w-full max-w-xl mx-4 rounded-xl bg-white shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900">Редактировать проект</h2>
            <p className="text-xs text-gray-500 mt-0.5">Шифр: {project.code}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Название объекта *</label>
            <input type="text" {...register('name')} maxLength={300} className={inputCls} />
            {errors.name && <p className={errorCls}>{errors.name.message}</p>}
            <CharCounter value={watch('name')} max={300} />
          </div>

          <div>
            <label className={labelCls}>Заказчик *</label>
            <input type="text" {...register('customer')} maxLength={300} className={inputCls} />
            {errors.customer && <p className={errorCls}>{errors.customer.message}</p>}
            <CharCounter value={watch('customer')} max={300} />
          </div>

          <div>
            <label className={labelCls}>Адрес объекта *</label>
            <input type="text" {...register('address')} maxLength={500} className={inputCls} />
            {errors.address && <p className={errorCls}>{errors.address.message}</p>}
            <CharCounter value={watch('address')} max={500} />
          </div>

          {canEditContractDates ? (
            <>
              <div>
                <label className={labelCls}>Шифр проекта *</label>
                <input type="text" {...register('code')} maxLength={50} className={inputCls} />
                {codeConflict && (
                  <p className="mt-1 text-xs text-amber-600">
                    Шифр уже используется в проекте «{codeConflict.name}» ({codeConflict.code})
                  </p>
                )}
                {errors.code && <p className={errorCls}>{errors.code.message}</p>}
                <CharCounter value={codeValue} max={50} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Дата начала *</label>
                  <input type="date" {...register('start_date')} className={inputCls} />
                  {errors.start_date && <p className={errorCls}>{errors.start_date.message}</p>}
                </div>
                <div>
                  <label className={labelCls}>Плановый срок завершения *</label>
                  <input type="date" {...register('planned_end_date')} className={inputCls} />
                  {errors.planned_end_date && (
                    <p className={errorCls}>{errors.planned_end_date.message}</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className={labelCls}>Плановый срок завершения</label>
              <input
                type="date"
                value={project.planned_end_date}
                disabled
                className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
              />
              <p className="mt-1 text-xs text-gray-400">Даты договора и шифр меняет только директор/зам</p>
            </div>
          )}

          {showCost && (
            <div>
              <label className={labelCls}>Стоимость проекта, ₽ (договорная)</label>
              <Controller
                name="cost"
                control={control}
                render={({ field }) => (
                  <MoneyInput
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="например 5 000 000"
                    className={inputCls}
                  />
                )}
              />
              {errors.cost && <p className={errorCls}>{errors.cost.message}</p>}
            </div>
          )}

          <div>
            <label className={labelCls}>ГИП</label>
            <select {...register('chief_engineer_id')} className={inputCls}>
              <option value=""> - не назначен - </option>
              {chiefEngineers.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>

          {/* Примечание */}
          <div>
            <label className={labelCls}>Примечание</label>
            <textarea
              {...register('note')}
              rows={3}
              maxLength={1000}
              placeholder="Любая заметка по проекту (необязательно)"
              className={`${inputCls} resize-y`}
            />
            <CharCounter value={watch('note')} max={1000} />
          </div>

          {/* Этап жизненного цикла */}
          <div>
            <label className={labelCls}>Этап проекта</label>
            <Controller
              name="lifecycle_stage"
              control={control}
              render={({ field }) => (
                <div className="flex gap-2 mt-1">
                  {LIFECYCLE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => field.onChange(opt.value)}
                      className={clsx(
                        'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all',
                        field.value === opt.value
                          ? opt.cls + ' ring-2 ring-offset-1 ring-current'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50',
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          {/* Ручной статус (цвет) проекта */}
          <div>
            <label className={labelCls}>
              Состояние проекта
              <span className="ml-1.5 text-xs font-normal text-gray-400">
                (Авто - вычисляется системой по срокам разделов)
              </span>
            </label>
            <Controller
              name="manual_color"
              control={control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2 mt-1">
                  {COLOR_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => field.onChange(opt.value)}
                      className={clsx(
                        'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-all',
                        field.value === opt.value
                          ? `border-transparent ring-2 ${opt.ring} bg-white font-medium text-gray-900`
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50',
                      )}
                    >
                      <span className={clsx('h-2.5 w-2.5 rounded-full flex-shrink-0', opt.dot)} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            />
            <p className="mt-1.5 text-xs text-gray-400">
              Ручной статус отображается везде: в списке, карточке, дашборде и Ганте.
            </p>
          </div>

          {/* Этапы проекта */}
          <div>
            <label className={labelCls}>Этапы проекта</label>
            <div className="space-y-2 mt-1">
              {DOC_TYPES.map((dt) => {
                const draft = phaseDrafts[dt] ?? { start_date: '', end_date: '', cost: '' }
                return (
                  <div key={dt} className={`grid ${showCost ? 'grid-cols-[56px_1fr_1fr_1fr]' : 'grid-cols-[56px_1fr_1fr]'} gap-2 items-center`}>
                    <span className="text-sm font-medium text-gray-700">{DOC_TYPE_LABELS[dt]}</span>
                    <input
                      type="date"
                      value={draft.start_date}
                      onChange={(e) => setPhaseDrafts((prev) => ({
                        ...prev,
                        [dt]: { ...draft, start_date: e.target.value },
                      }))}
                      title="Дата начала"
                      className={inputCls}
                    />
                    <input
                      type="date"
                      value={draft.end_date}
                      onChange={(e) => setPhaseDrafts((prev) => ({
                        ...prev,
                        [dt]: { ...draft, end_date: e.target.value },
                      }))}
                      title="Дата окончания"
                      className={inputCls}
                    />
                    {showCost && (
                      <input
                        type="number"
                        value={draft.cost}
                        onChange={(e) => setPhaseDrafts((prev) => ({
                          ...prev,
                          [dt]: { ...draft, cost: e.target.value },
                        }))}
                        placeholder="Стоимость"
                        title="Стоимость, руб."
                        className={inputCls}
                      />
                    )}
                  </div>
                )
              })}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {showCost
                ? 'Начало · Окончание · Стоимость (руб.) - для каждого этапа'
                : 'Начало · Окончание - для каждого этапа'}
            </p>
          </div>

          {saveError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
              {saveError}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
